import React from 'react';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/common/StatusBadge';
import { formatINR } from '../constants/status';

function InfoRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm font-semibold ${highlight ? 'text-brand-700' : 'text-slate-700'}`}>
        {value || '\u2014'}
      </span>
    </div>
  );
}

export default function ProfilePage() {
  const { fbUser, student, signOutUser } = useAuth();

  const loanAmount = student?.loanAmount ? formatINR(student.loanAmount) : null;
  const disbursedAmount = student?.disbursedAmount ? formatINR(student.disbursedAmount) : null;
  const sanctionedAmount = student?.sanctionedAmount ? formatINR(student.sanctionedAmount) : null;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-2xl mx-auto animate-fade-in">
      {/* Page header */}
      <h1 className="text-xl font-bold text-slate-800 mb-6">My Profile</h1>

      {/* User card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6 mb-5">
        <div className="flex items-center gap-4 mb-5">
          {fbUser?.photoURL ? (
            <img
              src={fbUser.photoURL}
              alt=""
              referrerPolicy="no-referrer"
              className="w-16 h-16 rounded-2xl object-cover ring-2 ring-slate-100"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center text-brand-600 font-bold text-xl">
              {student?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div>
            <h2 className="text-lg font-bold text-slate-800">{student?.name}</h2>
            <p className="text-sm text-slate-500">{student?.email}</p>
            {student?.phone && (
              <p className="text-sm text-slate-500">{student.phone}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={student?.status || 'New Lead'} />
          {student?.fileNumber && (
            <span className="text-xs text-slate-400 ml-2">
              File: <span className="font-medium text-slate-600">{student.fileNumber}</span>
            </span>
          )}
        </div>
      </div>

      {/* Course info */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6 mb-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Course Information</h3>
        <InfoRow label="Course" value={student?.course !== 'Pending' ? student?.course : null} />
        <InfoRow label="University" value={student?.university} />
        <InfoRow label="Country" value={student?.country !== 'TBD' ? student?.country : null} />
        <InfoRow label="Intake" value={student?.intakeMonth} />
      </div>

      {/* Loan info */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6 mb-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Loan Details</h3>
        <InfoRow label="Loan Amount" value={loanAmount} highlight />
        {sanctionedAmount && <InfoRow label="Sanctioned Amount" value={sanctionedAmount} highlight />}
        {disbursedAmount && <InfoRow label="Disbursed Amount" value={disbursedAmount} highlight />}
        <InfoRow label="Assigned Bank" value={student?.assignedBank} />
        <InfoRow label="Interested Bank" value={student?.interestedBank} />
      </div>

      {/* Sign out */}
      <button
        onClick={signOutUser}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        Sign Out
      </button>
    </div>
  );
}
